import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDropDownIcon, ArrowDropUpIcon, Stack, TextField, Typography } from '@esgian/esgianui';
import { Button, ButtonGroup } from '@mui/material';
import { parseNumberToSignificant } from '@helpers/numberFormatter';

type Props = {
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
};

function PercentInput({ value, onChange, disabled = false }: Props) {
  const [tempValue, setTempValue] = useState<string>(`${value}`);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setTempValue(`${value}`);
  }, [value]);

  const displayValue = useMemo(() => {
    if (tempValue === null || tempValue === undefined) {
      return 0;
    }

    return !isFocused ? parseNumberToSignificant(parseFloat(tempValue)) : tempValue;
  }, [tempValue, isFocused]);

  const handleChange = useCallback(
    (val: string, convertToValidNumber: boolean) => {
      const sanitizedInput = val.trim().replace(',', '.');

      // Check if the input is a valid number
      const numericValue = parseFloat(sanitizedInput);

      if (!isNaN(numericValue) && /^-?[0-9]+(\.[0-9]+)?$/.test(sanitizedInput)) {
        if (!convertToValidNumber && val === '-0') return;
        onChange(numericValue);
      }
    },
    [onChange]
  );

  return (
    <Stack spacing={1} direction={'row'} alignItems={'center'}>
      <Typography variant={'subtitle1'} bold>
        Update all by percentage:
      </Typography>
      <Stack direction={'row'} spacing={0.5}>
        <TextField
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            handleChange(tempValue, true);
          }}
          disabled={disabled}
          autoComplete={'off'}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(target.value, false);
            setTempValue(target.value);
          }}
          value={displayValue} // Display formatted number or empty
          sx={{ width: '50px' }}
          inputProps={{
            sx: { fontSize: '14px', textAlign: 'center' },
            inputMode: 'decimal' // Use appropriate input mode for numbers
          }}
          size={'small'}
          FormHelperTextProps={{ sx: { fontSize: '10px', marginTop: '0px' } }}
          variant={'standard'}
          InputProps={{
            endAdornment: <Typography variant={'caption'}>%</Typography>
          }}
        />
        <Stack direction={'column'}>
          <ButtonGroup orientation="vertical" size={'small'}>
            <Button
              disabled={disabled}
              onClick={() => {
                onChange(value + 1);
              }}
              sx={{ minWidth: '0.6em !important', padding: '0px' }}>
              <ArrowDropUpIcon
                fontSize={'small'}
                sx={{
                  height: '0.6em',
                  width: '0.6em'
                }}
              />
            </Button>
            <Button
              disabled={disabled}
              onClick={() => {
                onChange(value - 1);
              }}
              sx={{ minWidth: '0.7em !important', padding: '0px' }}>
              <ArrowDropDownIcon
                fontSize={'small'}
                sx={{
                  height: '0.6em',
                  width: '0.60em'
                }}
              />
            </Button>
          </ButtonGroup>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default PercentInput;
